"use client";

import { FC, useState, useCallback } from "react";

import { Tooltip } from "./tooltip";

interface AbbreviationProps {
  /**
   * The abbreviated text to display (e.g., "WAS", "CPWA")
   */
  abbr: string;
  /**
   * Optional language attribute for the abbreviation
   */
  lang?: string;
  /**
   * Optional prefix text to display before the abbreviation (e.g., "IAAP ")
   */
  prefix?: string;
  /**
   * The full explanation/expansion of the abbreviation (e.g., "Web Accessibility Specialist")
   */
  title: string;
}

/**
 * A reusable abbreviation component with tooltip explanation.
 * Provides keyboard and mouse accessibility with the same styling as certifications.
 */
export const Abbreviation: FC<AbbreviationProps> = ({
  abbr,
  lang = "sv",
  prefix,
  title,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const abbrRef = useCallback((node: HTMLElement | null) => {
    if (node) {
      setAnchorEl(node);
    }
  }, []);

  return (
    <>
      {prefix && <span>{prefix}</span>}
      <abbr
        lang={lang}
        ref={abbrRef}
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
        tabIndex={0}
      >
        <span>{abbr}</span>
      </abbr>
      <span className="visually-hidden">{` (${title})`}</span>
      <Tooltip anchorEl={anchorEl} title={title} />
    </>
  );
};

export default Abbreviation;
